<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row>
        <b-col>
          <h1>Sistema de Gestión de Impuestos</h1>
          <p class="lead">Gestiona catálogos de impuestos, reglas y configuraciones para el sistema POS.</p>
        </b-col>
      </b-row>

      <!-- Navigation Tabs -->
      <b-tabs content-class="mt-3">
        <b-tab title="Catálogos de Impuestos" active>
          <TaxCatalogManager />
        </b-tab>
        
        <b-tab title="Reglas de Impuestos">
          <TaxRuleEditor />
        </b-tab>
        
        <b-tab title="Resumen del Sistema">
          <b-card>
            <template #header>
              <h5 class="mb-0">Estadísticas del Sistema de Impuestos</h5>
            </template>
            
            <b-row>
              <b-col md="3">
                <b-card bg-variant="primary" text-variant="white" class="text-center">
                  <h2>{{ stats.totalCatalogs }}</h2>
                  <p class="mb-0">Catálogos de Impuestos</p>
                </b-card>
              </b-col>
              
              <b-col md="3">
                <b-card bg-variant="success" text-variant="white" class="text-center">
                  <h2>{{ stats.totalRules }}</h2>
                  <p class="mb-0">Reglas Configuradas</p>
                </b-card>
              </b-col>
              
              <b-col md="3">
                <b-card bg-variant="info" text-variant="white" class="text-center">
                  <h2>{{ stats.totalTaxLines }}</h2>
                  <p class="mb-0">Líneas de Impuesto</p>
                </b-card>
              </b-col>
              
              <b-col md="3">
                <b-card bg-variant="warning" text-variant="white" class="text-center">
                  <h2>{{ stats.activeRules }}</h2>
                  <p class="mb-0">Reglas Activas</p>
                </b-card>
              </b-col>
            </b-row>

            <hr>

            <h6>Configuración por Tipo de Impuesto</h6>
            <b-table 
              :items="taxTypeStats" 
              :fields="statsFields"
              striped hover small>
              <template #cell(tax_code)="row">
                <b-badge :variant="getTaxBadgeVariant(row.item.tax_code)">{{ row.item.tax_code }}</b-badge>
              </template>
            </b-table>
          </b-card>
        </b-tab>

        <b-tab title="Documentación">
          <b-card>
            <template #header>
              <h5 class="mb-0">Guía de Uso del Sistema de Impuestos</h5>
            </template>

            <div class="documentation">
              <h6>1. Configuración de Catálogos</h6>
              <p>Los catálogos de impuestos definen los tipos de impuestos disponibles en el sistema (IVA, IEPS, ISR, etc.). Cada catálogo puede tener múltiples líneas con diferentes tasas.</p>
              
              <h6>2. Gestión de Reglas</h6>
              <p>Las reglas de impuestos determinan qué impuestos se aplican a qué productos o categorías. Las reglas tienen prioridad y pueden ser específicas por proveedor.</p>
              
              <h6>3. Integración con Productos (Supply.vue)</h6>
              <ul>
                <li>Cada producto puede tener reglas de impuestos específicas</li>
                <li>Configuración especial para IEPS según el tipo de producto</li>
                <li>Gestión de exenciones y tasas especiales</li>
              </ul>
              
              <h6>4. Aplicación en Compras (Compra.vue)</h6>
              <ul>
                <li>Cálculo automático de impuestos en órdenes de compra</li>
                <li>Gestión manual de impuestos por artículo</li>
                <li>Recálculo automático cuando cambian precios o cantidades</li>
                <li>Visualización detallada de la aplicación de impuestos</li>
              </ul>

              <h6>5. Tipos de Impuestos Soportados</h6>
              <b-table :items="supportedTaxes" :fields="['code', 'name', 'description']" small striped>
                <template #cell(code)="row">
                  <b-badge :variant="getTaxBadgeVariant(row.item.code)">{{ row.item.code }}</b-badge>
                </template>
              </b-table>

              <h6>6. API Endpoints</h6>
              <div class="bg-light p-3">
                <code>GET /tax-catalogs</code> - Obtener catálogos<br>
                <code>POST /tax-catalogs</code> - Crear catálogo<br>
                <code>GET /supply-tax-rules</code> - Obtener reglas<br>
                <code>POST /supply-tax-rules</code> - Crear regla<br>
                <code>POST /purchase-orders/{id}/recalculate-taxes</code> - Recalcular impuestos<br>
                <code>GET /purchase-orders/{po_id}/items/{item_id}/taxes</code> - Impuestos de artículo<br>
                <code>POST /update-product-ieps-rate</code> - Actualizar tasa IEPS<br>
              </div>
            </div>
          </b-card>
        </b-tab>
      </b-tabs>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import TaxCatalogManager from "../components/TaxCatalogManager.vue";
import TaxRuleEditor from "../components/TaxRuleEditor.vue";
import axios from "axios";

export default {
  name: "TaxManagement",
  components: {
    Nav,
    TaxCatalogManager,
    TaxRuleEditor
  },
  data() {
    return {
      stats: {
        totalCatalogs: 0,
        totalRules: 0,
        totalTaxLines: 0,
        activeRules: 0
      },
      taxTypeStats: [],
      statsFields: [
        { key: 'tax_code', label: 'Tipo', sortable: true },
        { key: 'catalogs_count', label: 'Catálogos', sortable: true },
        { key: 'rules_count', label: 'Reglas', sortable: true },
        { key: 'lines_count', label: 'Líneas', sortable: true }
      ],
      supportedTaxes: [
        { 
          code: 'IVA', 
          name: 'Impuesto al Valor Agregado',
          description: 'Impuesto general sobre ventas, típicamente 16% en México'
        },
        { 
          code: 'IEPS', 
          name: 'Impuesto Especial sobre Producción y Servicios',
          description: 'Impuesto especial para productos como alcohol, tabaco, bebidas'
        },
        { 
          code: 'ISR', 
          name: 'Impuesto Sobre la Renta',
          description: 'Retención de impuesto sobre ingresos'
        },
        { 
          code: 'CEDULA', 
          name: 'Retención por Cédula',
          description: 'Sistema de retención clasificado por tipo de ingreso'
        },
        { 
          code: 'ICA', 
          name: 'Impuesto de Industria y Comercio',
          description: 'Impuesto municipal sobre actividades comerciales'
        }
      ]
    };
  },
  
  mounted() {
    this.loadStats();
  },
  
  methods: {
    async loadStats() {
      try {
        // Load tax catalogs
        const catalogsResponse = await axios.get('/tax-catalogs');
        this.stats.totalCatalogs = catalogsResponse.data.length;
        
        // Calculate total tax lines
        this.stats.totalTaxLines = catalogsResponse.data.reduce((sum, catalog) => {
          return sum + (catalog.tax_lines ? catalog.tax_lines.length : 0);
        }, 0);

        // Load tax rules
        const rulesResponse = await axios.get('/supply-tax-rules');
        this.stats.totalRules = rulesResponse.data.length;
        this.stats.activeRules = rulesResponse.data.filter(rule => rule.is_active).length;

        // Calculate stats by tax type
        this.calculateTaxTypeStats(catalogsResponse.data, rulesResponse.data);
        
      } catch (err) {
        console.error('Error loading stats:', err);
        this.makeToast('Error', 'Error al cargar estadísticas', 'danger');
      }
    },
    
    calculateTaxTypeStats(catalogs, rules) {
      const statsMap = {};
      
      // Initialize stats for each tax code
      catalogs.forEach(catalog => {
        if (!statsMap[catalog.tax_code]) {
          statsMap[catalog.tax_code] = {
            tax_code: catalog.tax_code,
            catalogs_count: 0,
            rules_count: 0,
            lines_count: 0
          };
        }
        
        statsMap[catalog.tax_code].catalogs_count++;
        statsMap[catalog.tax_code].lines_count += catalog.tax_lines ? catalog.tax_lines.length : 0;
      });
      
      // Count rules by tax catalog
      rules.forEach(rule => {
        if (rule.tax_catalog && rule.tax_catalog.tax_code) {
          const code = rule.tax_catalog.tax_code;
          if (statsMap[code]) {
            statsMap[code].rules_count++;
          }
        }
      });
      
      this.taxTypeStats = Object.values(statsMap);
    },
    
    getTaxBadgeVariant(taxCode) {
      const variants = {
        'IVA': 'primary',
        'IEPS': 'warning',
        'ISR': 'info',
        'CEDULA': 'secondary',
        'ICA': 'success'
      };
      return variants[taxCode] || 'light';
    },
    
    makeToast(title, message, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>

<style scoped>
.documentation h6 {
  color: #495057;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.documentation p, .documentation ul {
  margin-bottom: 1rem;
}

.documentation ul {
  padding-left: 1.5rem;
}

.documentation li {
  margin-bottom: 0.25rem;
}

.documentation code {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 0.2rem;
  color: #e83e8c;
}
</style>